import { memo } from 'react';
import { Link, useNavigate } from 'react-router';
import { type PreviewLine, previewLines, taskStats } from '@/domain/markdown';
import { displayTitle, type LocalNote } from '@/domain/note';
import { tagKey } from '@/domain/tags';
import { folderUrl } from '@/features/shell/FolderTree';
import { Icon } from '@/ui/Icon';

interface Props {
  note: LocalNote;
  onToggleTask: (id: string, line: number) => void;
}

/** Note preview card with interactive checkboxes. */
export const NoteCard = memo(function NoteCard({ note, onToggleTask }: Props) {
  const navigate = useNavigate();
  const lines = previewLines(note.content, 10);
  const stats = note.kind === 'todo' ? taskStats(note.content) : { done: 0, total: 0 };
  return (
    <article className={`note-card${note.pinned ? ' is-pinned' : ''}`} onClick={() => void navigate(`/note/${note.id}`)}>
      <CardHeader note={note} hasLines={lines.length > 0} />
      {lines.length > 0 && (
        <ul className="note-card__lines">
          {lines.map((l) => (
            <Line key={l.index} line={l} readOnly={note.role === 'viewer'} onToggle={() => onToggleTask(note.id, l.index)} />
          ))}
        </ul>
      )}
      {(note.tags.length > 0 || stats.total > 0) && (
        <footer className="note-card__footer">
          {stats.total > 0 && (
            <span className="note-card__progress">
              {stats.done}/{stats.total}
            </span>
          )}
          {note.tags.slice(0, 4).map((t) => (
            <Link key={t} className="tag-pill" to={folderUrl(tagKey(t))} onClick={(e) => e.stopPropagation()}>
              #{t}
            </Link>
          ))}
        </footer>
      )}
    </article>
  );
});

function CardHeader({ note, hasLines }: { note: LocalNote; hasLines: boolean }) {
  const title = note.title.trim();
  const visible = title !== '' || note.shared || note.pinned || !hasLines;
  let label = <span className="sr-only">{displayTitle(note)}</span>;
  if (title) label = <>{title}</>;
  else if (!hasLines) label = <span className="note-card__empty">Leere Notiz</span>;
  return (
    <header className={`note-card__header${visible ? '' : ' sr-only'}`}>
      <h3 className="note-card__title">
        <Link to={`/note/${note.id}`} onClick={(e) => e.stopPropagation()}>
          {label}
        </Link>
      </h3>
      <span className="note-card__badges">
        {note.shared && <Icon name="group" size={18} />}
        {note.pinned && <Icon name="keep_fill" size={18} />}
      </span>
    </header>
  );
}

function Line({ line, readOnly, onToggle }: { line: PreviewLine; readOnly: boolean; onToggle: () => void }) {
  const style = 'indent' in line ? { paddingInlineStart: line.indent * 16 } : undefined;
  return (
    <li className={`line line--${line.kind}`} style={style}>
      {line.kind === 'task' && (
        <button
          type="button"
          className={`line__check${line.checked ? ' is-checked' : ''}`}
          role="checkbox"
          aria-checked={line.checked}
          aria-label={line.text || 'Aufgabe'}
          disabled={readOnly}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <Icon name={line.checked ? 'check_box' : 'check_box_outline_blank'} size={20} />
          <span>{line.text}</span>
        </button>
      )}
      {line.kind === 'bullet' && <span className="line__marker">{line.marker}</span>}
      {line.kind !== 'task' && line.text}
    </li>
  );
}
